'use client'

import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { flushSync } from 'react-dom'
import { BackendAuth } from '@/lib/auth-backend'
import { isTrialSession, isTrialForbidden } from '@/lib/trial-session'

/**
 * NexusBackgroundProvider
 *
 * Provides global state for Nexus background tasks.
 * Uses SSE (Server-Sent Events) for real-time updates
 * and falls back to polling for new tasks detection.
 */

// Polling interval for checking new tasks (when no active tasks)
const NEW_TASK_CHECK_INTERVAL = 30000

// Reconnect delay for SSE on error
const SSE_RECONNECT_DELAY = 2000

const NexusBackgroundContext = createContext(null)

export function NexusBackgroundProvider({ children }) {
  const [activeTasks, setActiveTasks] = useState([])
  const [foregroundSessions, setForegroundSessions] = useState([]) // Active SSE streams
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [lifetimeCost, setLifetimeCost] = useState(0) // Total cost across all projects

  const pollIntervalRef = useRef(null)
  const isMountedRef = useRef(true)
  const trialBlockedRef = useRef(false) // true = trial account, ai-status not available
  const sseConnectionsRef = useRef(new Map()) // Map<taskId, AbortController>

  // Register a foreground SSE session (called when chat starts streaming)
  const registerForegroundSession = useCallback((session) => {
    setForegroundSessions(prev => {
      // Prevent duplicates
      if (prev.find(s => s.session_id === session.session_id)) {
        return prev.map(s => s.session_id === session.session_id ? { ...s, ...session } : s)
      }
      return [...prev, {
        ...session,
        status: 'running',
        is_foreground: true,
        started_at: new Date().toISOString()
      }]
    })
  }, [])

  // Update a foreground session (progress, cost, etc.)
  const updateForegroundSession = useCallback((sessionId, updates) => {
    setForegroundSessions(prev =>
      prev.map(s => s.session_id === sessionId ? { ...s, ...updates } : s)
    )
  }, [])

  // Unregister a foreground session (called when chat completes or stops)
  const unregisterForegroundSession = useCallback((sessionId) => {
    setForegroundSessions(prev => prev.filter(s => s.session_id !== sessionId))
  }, [])

  // Fetch active tasks from backend
  const fetchTasks = useCallback(async () => {
    // Trial sessions (website setup before purchase, /setup/<id>?trial=...) are not
    // allowed to query /api/nexus/ai-status (403 TRIAL_FORBIDDEN). Polling it every
    // 30s only produced a console error each time - skip it entirely.
    if (trialBlockedRef.current || isTrialSession()) {
      trialBlockedRef.current = true
      setActiveTasks([])
      setIsLoading(false)
      return
    }
    try {
      // Only fetch if user is authenticated
      if (!BackendAuth.isAuthenticated()) {
        setActiveTasks([])
        setIsLoading(false)
        return
      }

      const response = await BackendAuth.apiCallJson(
        '/api/nexus/ai-status?status=pending&status=claimed&status=running&status=paused'
      )

      if (!isMountedRef.current) return

      if (response && response.tasks) {
        setActiveTasks(response.tasks)
        setLifetimeCost(response.lifetime_cost_usd || 0)
        setError(null)
      } else {
        setActiveTasks([])
      }
    } catch (err) {
      if (isTrialForbidden(err)) {
        // Account has no access to background tasks - stop polling for good.
        trialBlockedRef.current = true
        setActiveTasks([])
        return
      }
      console.error('NexusBackgroundProvider: Error fetching tasks', err)
      setError(err.message)
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false)
        setLastUpdate(Date.now())
      }
    }
  }, [])

  // ============================================
  // SSE Connection Functions (MUST be defined FIRST)
  // ============================================

  // Connect to SSE stream for a specific task
  const connectToTaskSSE = useCallback(async (taskId) => {
    // Don't create duplicate connections
    if (sseConnectionsRef.current.has(taskId)) {
      return
    }

    const abortController = new AbortController()
    sseConnectionsRef.current.set(taskId, abortController)

    try {
      const token = BackendAuth.getToken()
      const response = await fetch(
        `/api/nexus/ai-stream/${taskId}/ai-chat`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'text/event-stream'
          },
          signal: abortController.signal
        }
      )

      if (!response.ok) {
        throw new Error(`SSE failed: ${response.status}`)
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let buffer = ''

      while (reader && isMountedRef.current) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6))

              // flushSync forces a synchronous React commit per event,
              // bypassing React 18 automatic batching for progressive task status updates.
              flushSync(() => setActiveTasks(prev => prev.map(t => {
                if (t.task_id !== taskId) return t

                // Update based on event type
                if (event.type === 'task_running') {
                  return { ...t, status: 'running' }
                }
                if (event.type === 'task_progress') {
                  return {
                    ...t,
                    progress_percent: event.progress_percent || t.progress_percent,
                    current_step: event.current_step || t.current_step,
                    estimated_cost_usd: event.cost_usd || t.estimated_cost_usd,
                    input_tokens: event.input_tokens || t.input_tokens,
                    output_tokens: event.output_tokens || t.output_tokens
                  }
                }
                if (event.type === 'task_completed' || event.type === 'task_failed' || event.type === 'task_cancelled') {
                  // Task finished - will be removed in next poll
                  return { ...t, status: event.type.replace('task_', '') }
                }
                return t
              })))

              // Remove completed/failed/cancelled tasks after a short delay
              if (['task_completed', 'task_failed', 'task_cancelled'].includes(event.type)) {
                // Will be disconnected and removed - no need for reconnect
                setTimeout(() => {
                  setActiveTasks(prev => prev.filter(t => t.task_id !== taskId))
                }, 3000) // Keep visible for 3s
                return // Exit the loop
              }

            } catch (parseErr) {
              console.warn('[NexusSSE] Parse error:', line)
            }
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error(`[NexusSSE] Error for task ${taskId}:`, err)
        // Will be reconnected by the effect if task is still active
      }
    } finally {
      sseConnectionsRef.current.delete(taskId)
    }
  }, []) // No dependencies - uses refs for all external state

  // Disconnect SSE for a specific task
  const disconnectTaskSSE = useCallback((taskId) => {
    const controller = sseConnectionsRef.current.get(taskId)
    if (controller) {
      controller.abort()
      sseConnectionsRef.current.delete(taskId)
    }
  }, [])

  // Disconnect all SSE connections
  const disconnectAllSSE = useCallback(() => {
    for (const [taskId, controller] of sseConnectionsRef.current.entries()) {
      controller.abort()
    }
    sseConnectionsRef.current.clear()
  }, [])

  // ============================================
  // Task Management Functions (can reference SSE functions)
  // ============================================

  // Create a background task
  const createBackgroundTask = useCallback(async (projectId, message, context = null) => {
    try {
      const response = await BackendAuth.apiCallJson('/api/nexus/ai-stream', {
        method: 'POST',
        body: JSON.stringify({
          project_id: projectId,
          message: message,
          context: context,
          task_type: 'chat'
        })
      })

      if (response && response.success) {
        // Refresh task list to get the new task
        await fetchTasks()

        // Connect to SSE for the new task immediately
        if (response.task_id) {
          connectToTaskSSE(response.task_id)
        }

        return response
      } else {
        throw new Error(response?.error || 'Failed to create task')
      }
    } catch (err) {
      console.error('NexusBackgroundProvider: Error creating task', err)
      throw err
    }
  }, [fetchTasks, connectToTaskSSE])

  // Cancel a task
  const cancelTask = useCallback(async (taskId) => {
    try {
      // Disconnect SSE first
      disconnectTaskSSE(taskId)

      const response = await BackendAuth.apiCallJson(
        `/api/nexus/ai-stream/${taskId}/cancel`,
        { method: 'POST' }
      )

      if (response && response.success) {
        // Remove from local state immediately
        setActiveTasks(prev => prev.filter(t => t.task_id !== taskId))
        // Then refresh from server
        await fetchTasks()
        return true
      } else {
        throw new Error(response?.error || 'Failed to cancel task')
      }
    } catch (err) {
      console.error('NexusBackgroundProvider: Error cancelling task', err)
      throw err
    }
  }, [fetchTasks, disconnectTaskSSE])

  // Pause a task (worker will skip it and continue with next)
  const pauseTask = useCallback(async (taskId) => {
    try {
      const response = await BackendAuth.apiCallJson(
        `/api/nexus/ai-stream/${taskId}/pause`,
        { method: 'POST' }
      )

      if (response && response.success) {
        // Update local state
        setActiveTasks(prev => prev.map(t =>
          t.task_id === taskId ? { ...t, status: 'paused' } : t
        ))
        return true
      } else {
        throw new Error(response?.error || 'Failed to pause task')
      }
    } catch (err) {
      console.error('NexusBackgroundProvider: Error pausing task', err)
      throw err
    }
  }, [])

  // Resume a paused task
  const resumeTask = useCallback(async (taskId) => {
    try {
      const response = await BackendAuth.apiCallJson(
        `/api/nexus/ai-stream/${taskId}/resume`,
        { method: 'POST' }
      )

      if (response && response.success) {
        // Update local state
        setActiveTasks(prev => prev.map(t =>
          t.task_id === taskId ? { ...t, status: 'pending' } : t
        ))
        // Refresh to get new queue position
        await fetchTasks()
        return true
      } else {
        throw new Error(response?.error || 'Failed to resume task')
      }
    } catch (err) {
      console.error('NexusBackgroundProvider: Error resuming task', err)
      throw err
    }
  }, [fetchTasks])

  // Check task limits
  const checkLimits = useCallback(async () => {
    try {
      if (!BackendAuth.isAuthenticated()) {
        return { can_create_task: false, current_active_tasks: 0, max_concurrent_tasks: 1 }
      }

      const response = await BackendAuth.apiCallJson('/api/nexus/ai-stream/limits/check')
      return response
    } catch (err) {
      console.error('NexusBackgroundProvider: Error checking limits', err)
      return { can_create_task: false, error: err.message }
    }
  }, [])

  // Get task events (for detailed view)
  const getTaskEvents = useCallback(async (taskId, afterId = 0) => {
    try {
      const response = await BackendAuth.apiCallJson(
        `/api/nexus/ai-stream/${taskId}/events?after_id=${afterId}`
      )
      return response
    } catch (err) {
      console.error('NexusBackgroundProvider: Error getting task events', err)
      return { events: [], error: err.message }
    }
  }, [])

  // ============================================
  // Effects
  // ============================================

  // Initial setup and cleanup
  useEffect(() => {
    isMountedRef.current = true

    // Initial fetch
    fetchTasks()

    // Cleanup
    return () => {
      isMountedRef.current = false
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
      disconnectAllSSE()
    }
  }, [fetchTasks, disconnectAllSSE])

  // Connect to SSE for each active task
  useEffect(() => {
    // Get active task IDs
    const activeTaskIds = activeTasks
      .filter(t => ['pending', 'claimed', 'running'].includes(t.status))
      .map(t => t.task_id)

    // Connect to SSE for new tasks
    for (const taskId of activeTaskIds) {
      if (!sseConnectionsRef.current.has(taskId)) {
        connectToTaskSSE(taskId)
      }
    }

    // Disconnect SSE for tasks no longer active
    for (const [taskId] of sseConnectionsRef.current.entries()) {
      if (!activeTaskIds.includes(taskId)) {
        disconnectTaskSSE(taskId)
      }
    }
  }, [activeTasks, connectToTaskSSE, disconnectTaskSSE])

  // Slow polling just to check for new tasks (not for updates)
  useEffect(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
    }

    // Poll to detect new tasks (SSE handles status updates for existing tasks)
    pollIntervalRef.current = setInterval(() => {
      if (isMountedRef.current && BackendAuth.isAuthenticated()) {
        fetchTasks()
      }
    }, NEW_TASK_CHECK_INTERVAL)

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [fetchTasks])

  // ============================================
  // Computed values and context
  // ============================================

  // Computed values - combine background tasks and foreground sessions
  const computed = useMemo(() => {
    // Combine background tasks and foreground sessions for unified display
    const allTasks = [
      ...activeTasks,
      ...foregroundSessions.map(s => ({
        task_id: s.session_id,
        project_id: s.project_id,
        project_name: s.project_name || `Projekt ${s.project_id}`,
        status: s.status || 'running',
        progress_percent: s.progress_percent || 0,
        current_step: s.current_step || 'Streaming...',
        estimated_cost_usd: s.cost_usd || 0,
        project_total_cost_usd: s.project_total_cost_usd || 0,
        created_at: s.started_at,
        is_foreground: true  // Mark as foreground for different handling
      }))
    ]

    const hasActiveTasks = allTasks.length > 0

    // Calculate current session cost (aktuell)
    const totalCost = allTasks.reduce((sum, task) => {
      return sum + (task.estimated_cost_usd || 0)
    }, 0)

    // Calculate max project cost from active tasks (for "projekt" display)
    // Use the highest project_total_cost_usd from all active tasks
    const maxProjectCost = allTasks.reduce((max, task) => {
      return Math.max(max, task.project_total_cost_usd || 0)
    }, 0)

    // Count by status
    const runningCount = allTasks.filter(t => t.status === 'running').length
    const pendingCount = allTasks.filter(t => t.status === 'pending').length
    const claimedCount = allTasks.filter(t => t.status === 'claimed').length
    const pausedCount = allTasks.filter(t => t.status === 'paused').length
    const foregroundCount = foregroundSessions.length

    return {
      hasActiveTasks,
      totalCost,
      maxProjectCost,
      runningCount,
      pendingCount,
      claimedCount,
      pausedCount,
      foregroundCount,
      activeCount: allTasks.length,
      allTasks  // Combined list for display
    }
  }, [activeTasks, foregroundSessions])

  // Add lifetimeCost to context separately (not in useMemo to avoid re-renders)
  const lifetimeCostValue = lifetimeCost

  // Context value
  const value = useMemo(() => ({
    // State
    activeTasks: computed.allTasks,  // Use combined list
    foregroundSessions,
    isLoading,
    error,
    lastUpdate,
    lifetimeCost: lifetimeCostValue,  // Total cost across all projects

    // Computed
    ...computed,

    // Actions - Background Tasks
    createBackgroundTask,
    cancelTask,
    pauseTask,
    resumeTask,
    checkLimits,
    getTaskEvents,
    refreshTasks: fetchTasks,

    // Actions - Foreground Sessions (SSE streams)
    registerForegroundSession,
    updateForegroundSession,
    unregisterForegroundSession
  }), [
    computed,
    foregroundSessions,
    isLoading,
    error,
    lastUpdate,
    lifetimeCostValue,
    createBackgroundTask,
    cancelTask,
    pauseTask,
    resumeTask,
    checkLimits,
    getTaskEvents,
    fetchTasks,
    registerForegroundSession,
    updateForegroundSession,
    unregisterForegroundSession
  ])

  return (
    <NexusBackgroundContext.Provider value={value}>
      {children}
    </NexusBackgroundContext.Provider>
  )
}

/**
 * Hook to access Nexus background task state and actions.
 *
 * @returns {Object} Context with:
 *   - activeTasks: Array of active tasks
 *   - hasActiveTasks: Boolean if any tasks active
 *   - totalCost: Total estimated cost in USD
 *   - runningCount, pendingCount: Task counts
 *   - createBackgroundTask(projectId, message, context): Create new task
 *   - cancelTask(taskId): Cancel a task
 *   - checkLimits(): Check user's task limits
 *   - getTaskEvents(taskId, afterId): Get task events
 *   - refreshTasks(): Manually refresh task list
 */
export function useNexusBackground() {
  const ctx = useContext(NexusBackgroundContext)
  if (!ctx) {
    throw new Error('useNexusBackground must be used within NexusBackgroundProvider')
  }
  return ctx
}
